import React, { FunctionComponent, ReactElement, useEffect, useState } from 'react'

import ReactDatePicker from 'react-datepicker'

import 'react-datepicker/dist/react-datepicker.css'

import { endOfMonth } from 'date-fns'
import { enGB, lt, lv, nl, pl, ru } from 'date-fns/locale'

import CalendarIcon from '@/atoms/icons/calendar.svg'
import {
  DATE_DISPLAY_FORMAT,
  DATE_INPUT_FORMAT,
  DATE_INPUT_MASK,
  DATE_PLACEHOLDER,
  getMaxDate,
  getMinDate,
  getValidDate,
  MONTH_DISPLAY_FORMAT,
  MONTH_INPUT_FORMAT,
  MONTH_INPUT_MASK,
  MONTH_PLACEHOLDER,
} from '@/helpers/date'
import { AdornmentPosition } from '@/models/common'
import i18n from '@/translations'

import { MonthpickerHeader, MonthpickerHeaderProps, StyledDatePicker } from './atoms'
import PatternInput from './pattern-input'

const locales = {
  lt,
  lv,
  nl,
  en: enGB,
  ru,
  pl,
}

interface Props {
  id: string
  hasError?: boolean
  maxDate?: Date | string
  minDate?: Date | string
  startDate?: Date | string
  selected?: Date | string
  onChange?: (date: Date | Array<Date>) => void
  lastDay?: boolean
  disabled?: boolean
}

const MonthPickerComponent: FunctionComponent<Props> = ({
  maxDate: initialMaxDate,
  minDate: initialMinDate,
  startDate,
  onChange,
  id,
  hasError,
  lastDay,
  disabled,
}) => {
  const calculateDate = (date: Date | string) => {
    const validDate = getValidDate(date)
    if (!validDate) {
      return null
    }

    return lastDay ? endOfMonth(validDate) : validDate
  }

  const [start, setStartDate] = useState(calculateDate(startDate))
  const minDate = getMinDate(initialMinDate)
  const maxDate = getMaxDate(initialMaxDate)

  useEffect(() => {
    setStartDate(calculateDate(startDate))
  }, [startDate])

  const getCustomHeader = (props: MonthpickerHeaderProps): ReactElement => (
    <MonthpickerHeader params={props} locale={locales[i18n.language]} minDate={minDate} maxDate={maxDate} />
  )
  const handleChangeDate = (date: Date | Array<Date>): void => {
    setStartDate(calculateDate(date as Date))
    onChange(calculateDate(date as Date))
  }

  return (
    <StyledDatePicker>
      <ReactDatePicker
        placeholderText={lastDay ? DATE_PLACEHOLDER : MONTH_PLACEHOLDER}
        dateFormat={lastDay ? DATE_DISPLAY_FORMAT : MONTH_DISPLAY_FORMAT}
        showPopperArrow={false}
        popperPlacement="bottom"
        startDate={start}
        onChange={handleChangeDate}
        customInput={
          <PatternInput
            id={id}
            error={hasError}
            adornment={<CalendarIcon />}
            adornmentPosition={AdornmentPosition.START}
            format={lastDay ? DATE_INPUT_FORMAT : MONTH_INPUT_FORMAT}
            mask={lastDay ? DATE_INPUT_MASK : MONTH_INPUT_MASK}
          />
        }
        locale={locales[i18n.language]}
        maxDate={maxDate}
        minDate={minDate}
        renderCustomHeader={getCustomHeader}
        selected={start}
        showMonthYearPicker
        showFullMonthYearPicker
        disabled={disabled}
      />
    </StyledDatePicker>
  )
}

export default MonthPickerComponent
